/**
 * Grammar (shape only, meaning is the solver's job on replay):
 *   evolution-path = token ("," token)*
 *   token          = evoId [":" option ("." option)*]
 * Throws on anything else.
 */
export function parseEvolutionPath(value) {
    const reject = () => {
        throw new Error(`Malformed evolution-path "${value}"`);
    };
    const toInt = (part) => /^\d+$/.test(part) ? Number(part) : reject();
    if (typeof value !== "string" || value.length === 0)
        reject();
    const evolutionPath = [];
    const pathwayPath = [];
    for (const token of value.split(",")) {
        const [evoId = "", options, ...rest] = token.split(":");
        if (rest.length > 0 || options === "")
            reject();
        evolutionPath.push(toInt(evoId));
        pathwayPath.push(options === undefined ? [] : options.split(".").map(toInt));
    }
    return { evolutionPath, pathwayPath };
}
export function serializeEvolutionPath({ evolutionPath, pathwayPath, }) {
    if (evolutionPath.length !== pathwayPath.length) {
        throw new Error("evolutionPath and pathwayPath must have the same length");
    }
    return evolutionPath
        .map((id, step) => {
        const options = pathwayPath[step] ?? [];
        return options.length ? `${id}:${options.join(".")}` : `${id}`;
    })
        .join(",");
}
/** Every step linear: the pathway of a path with no option choices. */
export const linearPathwayPath = (pathLength) => Array.from({ length: pathLength }, () => []);
/**
 * The param as read off a URL. Absent means the base card; a malformed value
 * is treated the same rather than crashing the page it was pasted into.
 */
export const evolutionPathFromParam = (value) => {
    if (!value)
        return undefined;
    try {
        return parseEvolutionPath(value);
    }
    catch {
        return undefined;
    }
};
/**
 * The pair for a player whose path is known but whose pathway may not be
 * (older rows, in-progress builds): a missing or length-mismatched pathway
 * means every step linear. Request bodies send this pair as is.
 */
export const evolutionPathPair = (evolutionPath = [], pathwayPath) => ({
    evolutionPath,
    pathwayPath: pathwayPath?.length === evolutionPath.length
        ? pathwayPath
        : linearPathwayPath(evolutionPath.length),
});
/**
 * The chain a player row carries, or undefined for a base card. Reads
 * `evolutionData` first, as compare does: the evolutions list nests it there.
 */
export const playerEvolutionPath = (player) => {
    const evolutionPath = player.evolutionData?.evolutionPath ?? player.evolutionPath;
    if (!evolutionPath?.length)
        return undefined;
    return evolutionPathPair(evolutionPath, player.evolutionData?.pathwayPath ?? player.pathwayPath);
};
/** `playerEvolutionPath` as an `evolution-path` param; undefined for a base card. */
export const playerEvolutionPathParam = (player) => {
    const path = playerEvolutionPath(player);
    return path && serializeEvolutionPath(path);
};
/** The URL param form of `evolutionPathPair`. */
export const evolutionPathParam = (evolutionPath, pathwayPath) => serializeEvolutionPath(evolutionPathPair(evolutionPath, pathwayPath));
//# sourceMappingURL=evolutionPath.js.map